import type React from 'react';
import type { ShapeSelection, RenderNode, ShapeType } from '@/types/commontypes';

/**
 * 키보드 화살표 이동에 사용되는 loose 좌표 타입.
 * RenderNode의 다양한 variant가 갖는 좌표 필드를 제네릭하게 접근하기 위해 정의.
 */
type LooseCoordNode = {
  start?: [number, number];
  end?: [number, number];
  x1?: number;
  x2?: number;
  startXY?: [number, number];
  endXY?: [number, number];
  points?: [number, number][];
  position?: [number, number];
};

export const handleShapeMoveArrow = (
  direction: number,
  selectedShape: ShapeSelection | null,
  renderNodes: RenderNode[],
) => {
  const { uuid } = selectedShape!;
  const updatedShapes = renderNodes.map((shape: RenderNode): RenderNode => {
    if (shape.uuid !== uuid || shape.locked) return shape;

    // RFC 경계: 도형별 좌표 필드에 제네릭하게 접근하기 위해 loose 타입으로 캐스트
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const s = shape as unknown as LooseCoordNode;

    if (s.start && s.end) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return {
        ...shape,
        start: [s.start[0] + direction, s.start[1]] as [number, number],
        end: [s.end[0] + direction, s.end[1]] as [number, number],
        updatedAt: Date.now(),
      } as RenderNode;
    }

    if (s.x1 !== undefined && s.x2 !== undefined) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return {
        ...shape,
        x1: s.x1 + direction,
        x2: s.x2 + direction,
        updatedAt: Date.now(),
      } as RenderNode;
    }

    if (s.startXY && s.endXY) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return {
        ...shape,
        startXY: [s.startXY[0] + direction, s.startXY[1]],
        endXY: [s.endXY[0] + direction, s.endXY[1]],
        updatedAt: Date.now(),
      } as RenderNode;
    }

    if (s.points) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return {
        ...shape,
        points: s.points.map(([x, y]: [number, number]) => [x + direction, y]),
        updatedAt: Date.now(),
      } as RenderNode;
    }

    if (s.position) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return {
        ...shape,
        position: [s.position[0] + direction, s.position[1]],
        updatedAt: Date.now(),
      } as RenderNode;
    }

    return shape;
  });
  return updatedShapes;
};

export const cancelDrawing = (
  shapeTypeToDraw: ShapeType | undefined,
  chartCanvasRef: React.RefObject<{ cancelDrag?: () => void } | null>,
  shapeRefs: React.RefObject<Record<string, unknown> | null>,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const shape = shapeRefs.current?.[shapeTypeToDraw as string] as { terminate?: () => void } | undefined;
  if (shape?.terminate) shape.terminate();
  if (chartCanvasRef.current?.cancelDrag) chartCanvasRef.current.cancelDrag();
};
